import Image from 'next/image'
import { Container } from 'react-grid-system'
import { Button, Heading, Paragraph } from '~elements'
import Video from 'components/elements/Video/Video'
import { AssetType, ButtonProps, ResponsiveMultimedia, RichTextType } from '~types'
import styles from './HeroEdpHeader.module.scss'
import { NEXT_IMAGE_QUALITY } from '~constants'
import breakpoints from '../../../styles/breakpoints.json'

interface HeroEdpHeaderProps {
  buttonGroup: Array<ButtonProps>
  ctaSection?: Array<ctaSectionProps>
  description: RichTextType
  readMoreField: RichTextType
  subHeadline: string
  title: string
  responsiveMultimedia: ResponsiveMultimedia
  stackButtonsOnMobile?: boolean
}
interface ctaSectionProps {
  buttonText: string
  component: string
  id: string
  urlBase: string
}

function isImagePropsWithUrl(asset: unknown): asset is AssetType {
  return typeof asset === 'object' && asset !== null && 'url' in asset && typeof asset.url === 'string'
}

const HeroEdpHeader = ({
  buttonGroup,
  description,
  readMoreField,
  subHeadline = '',
  title,
  responsiveMultimedia,
  stackButtonsOnMobile
}: HeroEdpHeaderProps) => {
  const { posterImageSm, posterImageMd, posterImageXl, backgroundVideoSm, backgroundVideoMd, backgroundVideoXl } =
    responsiveMultimedia ?? {}

  const fallbackImage = posterImageSm ?? posterImageMd ?? posterImageXl
  const fallbackAlt = posterImageSm?.altText ?? posterImageMd?.altText ?? posterImageXl?.altText ?? title ?? ''

  const hasVideo = Boolean(backgroundVideoXl ?? backgroundVideoMd ?? backgroundVideoSm)

  const doesBackgroundImageHasURL = isImagePropsWithUrl(fallbackImage)

  const responsiveMediaWrapperClass = doesBackgroundImageHasURL ? styles['hero__multimedia-wrapper'] : ''
  const heroModifierClass = doesBackgroundImageHasURL ? styles['hero-container-wrapper'] : ''

  const handleButtonAnchor = (elementID: string) => {
    const targetElement = document.getElementById(elementID)
    if (targetElement != null) {
      targetElement.scrollIntoView({
        behavior: 'smooth'
      })
    }
  }

  return (
    <div className={styles['hero']}>
      <div className={`${styles['hero__media-wrapper']} ${responsiveMediaWrapperClass}`}>
        {hasVideo ? (
          <Video
            videoLoop={responsiveMultimedia?.videoLoop ?? false}
            backgroundVideoXl={backgroundVideoXl}
            backgroundVideoMd={backgroundVideoMd}
            backgroundVideoSm={backgroundVideoSm}
            posterImageXl={posterImageXl}
            posterImageMd={posterImageMd}
            posterImageSm={posterImageSm}
          />
        ) : responsiveMultimedia ? (
          <picture>
            {posterImageXl?.url ? (
              <source srcSet={posterImageXl.url} media={`(min-width: ${breakpoints.lg}px)`} />
            ) : (
              <></>
            )}
            {posterImageMd?.url ? (
              <source srcSet={posterImageMd.url} media={`(min-width: ${breakpoints.md}px)`} />
            ) : (
              <></>
            )}
            {doesBackgroundImageHasURL && fallbackImage && (
              <Image
                src={fallbackImage.url}
                alt={fallbackAlt}
                sizes='100vw'
                width={0}
                height={0}
                style={{
                  width: '100%',
                  height: '100%'
                }}
                quality={NEXT_IMAGE_QUALITY}
                priority
              />
            )}
          </picture>
        ) : (
          fallbackImage &&
          typeof fallbackImage === 'string' && (
            <Image
              src={fallbackImage}
              alt={fallbackAlt}
              sizes='100vw'
              width={0}
              height={0}
              style={{
                width: '100%',
                height: '100%'
              }}
              quality={NEXT_IMAGE_QUALITY}
              priority
            />
          )
        )}
      </div>
      <div className={`${styles['hero__copy']} ${heroModifierClass}`}>
        <Container
          className={'container container--half-padding-xs'}
          // zIndex so it overlaps absolutely positioned image
          style={{ zIndex: 2 }}
        >
          <div className={styles['header-title-container']}>
            <div>
              {title && <Heading level={1}>{title}</Heading>}
              {subHeadline && (
                <Heading level={2} levelDisplay='eyebrow' color='accent'>
                  {subHeadline}
                </Heading>
              )}
            </div>
            {buttonGroup && (
              <div
                className={`${styles['btn-container']} ${stackButtonsOnMobile ? styles.stacked : styles.horizontal}`}
              >
                {buttonGroup.map((button, index) => (
                  <Button
                    key={index}
                    {...button}
                    {...(button?.anchorLink && {
                      onClick: () => handleButtonAnchor(button.anchorLink ?? '')
                    })}
                    analytics={{
                      linkModule: HeroEdpHeader.displayName,
                      clickTracking: `edp_hero:${button.title}:cta`
                    }}
                  />
                ))}
              </div>
            )}
          </div>
          {description && (
            <div className={styles['description-container']}>
              <Paragraph text={description} fontSize={'md-plus'} readMoreText={readMoreField} />
            </div>
          )}
        </Container>
      </div>
    </div>
  )
}

HeroEdpHeader.displayName = 'hero_edp'

export default HeroEdpHeader
